import ExportService from '../../../services/exportService.js';
import PdfService from '../../../services/pdfService.js';
import { ExportCoreUtils } from '../ExportButton/ExportCore.js';

export const ExportTypes = {
    CREATIVES: 'Creatives',
    PICTURES: 'Pictures',
    CAMPAIGN_EXPERIENCE: 'CampaignExperience',
    MAP_URL: 'URLToMap',
    ADDRESS_LIST: 'AddressList',
    CAMPAIGN_EFFECT: 'CampaignEffectMeasure'
};

// Each export strategy implements a canExecute and execute method
const exportStrategies = {
    [ExportTypes.CREATIVES]: {
        label: 'Creatives',
        exportType: 'zip',
        canExecute: (props) => !!(props.mediaArray && props.mediaArray.length > 0),
        execute: async (props) => {
            try {
                const data = await ExportService.downloadCampaignMaterial(
                    props.campaignName || 'campaign',
                    props.mediaArray
                );

                // Use current date for the filename
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                const fileName = `${props.campaignName || 'campaign'}_creatives_${timestamp}.zip`;

                await ExportCoreUtils.handleDirectFileDownload(
                    data,
                    fileName,
                    ExportCoreUtils.getMimeType('zip')
                );

                return { success: true, fileName };
            } catch (error) {
                console.error('Failed to download campaign ad-copies:', error);
                throw error;
            }
        }
    },

    [ExportTypes.PICTURES]: {
        label: 'Pictures',
        exportType: 'zip',
        canExecute: (props) => !!(props.panelPictures && props.panelPictures.length > 0),
        execute: async (props) => {
            try {
                const pictureInfoList = props.panelPictures.map(picture => ({
                    filename: picture.orgFileName,
                    url: picture.storedAs
                }));

                const data = await ExportService.downloadPanelPictures(
                    props.campaignName || 'campaign',
                    pictureInfoList
                );

                // Use current date for the filename
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                const fileName = `${props.campaignName || 'campaign'}_pictures_${timestamp}.zip`;

                await ExportCoreUtils.handleDirectFileDownload(
                    data,
                    fileName,
                    ExportCoreUtils.getMimeType('zip')
                );

                return { success: true, fileName };
            } catch (error) {
                console.error('Failed to download campaign pictures:', error);
                throw error;
            }
        }
    },

    [ExportTypes.CAMPAIGN_EXPERIENCE]: {
        label: 'Campaign Experience',
        exportType: 'zip',
        canExecute: (props) => !!(props.campaignExperienceMedia && props.campaignExperienceMedia.length > 0),
        execute: async (props) => {
            try {
                const campaignExperienceInfoList = props.campaignExperienceMedia.map(media => ({
                    filename: media.filename,
                    url: media.url
                }));

                const data = await ExportService.downloadCampaignMaterial(
                    props.campaignName || 'campaign',
                    campaignExperienceInfoList
                );

                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                const fileName = `${props.campaignName || 'campaign'}_campaign_experience_${timestamp}.zip`;

                await ExportCoreUtils.handleDirectFileDownload(
                    data,
                    fileName,
                    ExportCoreUtils.getMimeType('zip')
                );

                return { success: true, fileName };
            } catch (error) {
                console.error('Failed to download campaign experience:', error);
                throw error;
            }
        }
    },

    [ExportTypes.MAP_URL]: {
        label: 'URL to Map',
        exportType: 'url',
        canExecute: (props) => !!(props._mapInstance && props._mapInstance.saveMap),
        execute: async (props) => {
            try {
                // This is unique as it returns a URL, not a file to download
                const url = await props._mapInstance.saveMap();
                // console.log('Map URL exported:', url);
                return url;
            } catch (error) {
                console.error('Error saving map:', error);
                throw error;
            }
        }
    },

    [ExportTypes.ADDRESS_LIST]: {
        label: 'Address list',
        exportType: 'excel',
        canExecute: (props) => {
            return !!(props.panels && props.panels.length > 0);
        },
        execute: async (props) => {
            try {
                const exportHandler = ExportService.createAddressListExportHandler({
                    panels: props.panels,
                    orderData: props.orderData,
                    selectedOrderlineMetaData: props.selectedOrderlineMetaData,
                    campaignType: props.campaignType,
                    campaignName: props.campaignName,
                    campaignPromiseType: props.campaignPromiseType,
                    exportPanelSOVValue: props.exportPanelSOVValue || false,
                    exportPanelVACValue: props.exportPanelVACValue || false
                });

                return await exportHandler();
            } catch (error) {
                console.error('Error creating address list export:', error);
                throw error;
            }
        }
    },

    [ExportTypes.CAMPAIGN_EFFECT]: {
        label: 'Campaign effect measure',
        exportType: 'pdf',
        canExecute: (props) => !!props.onExportCampaignEffect,
        execute: async (props) => {
            try {
                var effectMeasure = props.selectedOrderlineMetaData[0].effectMeasure;
                var data = await PdfService.getEffectMeasurePdf(effectMeasure.storedAs);
                await ExportCoreUtils.handleDirectFileDownload(
                    data,
                    effectMeasure.orgFileName,
                    ExportCoreUtils.getMimeType('pdf')
                );
            } catch (error) {
                console.error('Error exporting campaign effect measure:', error);
                throw error;
            }
        }
    }
};

/**
 * Returns list of available export options based on the provided props
 * @param {Object} props - Props available for export operations
 * @param {Array<string>} disabledExports - Optional array of export types to disable
 * @returns {Array<{key: string, label: string}>} - Available export options
 */
export const getAvailableExports = (props, disabledExports = []) => {
    return Object.entries(exportStrategies)
        .filter(([key, strategy]) => !disabledExports.includes(key) && strategy.canExecute(props))
        .map(([key, strategy]) => ({
            key,
            label: strategy.label
        }));
};

/**
 * Executes an export operation of the specified type
 * @param {string} exportType - Type of export to execute (from ExportTypes)
 * @param {Object} props - Props required for the export operation
 * @returns {Promise<any>} - Promise that resolves when the export completes
 */
export const executeExport = async (exportType, props) => {
    try {
        // Validate export type
        const strategy = exportStrategies[exportType];
        if (!strategy) {
            console.warn(`Export type ${exportType} not found`);
            return Promise.reject(new Error(`Export type ${exportType} not found`));
        }

        // Check if export can be executed with provided props
        if (!strategy.canExecute(props)) {
            console.warn(`Export type ${exportType} cannot be executed with the provided props`);
            return Promise.reject(new Error(`Export type ${exportType} cannot be executed with the provided props`));
        }

        // Execute the export strategy
        return await strategy.execute(props);
    } catch (error) {
        console.error(`Error executing ${exportType} export:`, error);
        return Promise.reject(new Error(`Export ${exportType} failed: ${error.message || 'Unknown error'}`));
    }
};